/**
 * Matches and the party statement (Phase 4 of docs/plans/ledger.md), the pure
 * half: which open lines a new line pays, oldest first (L21), and a party's
 * statement worked out from its lines and their matches. The server reads
 * the lines and writes the matches; this file only decides.
 *
 * Amounts are exact: every figure is worked as an integer at nine decimal
 * places, enough for any currency and any quantity, and leaves as a decimal
 * string with no trailing noughts.
 */
import { toMinor } from '../document-math';
import { minorToDecimal } from '../doc-format';
import type { LineMatchView, StatementLine, StatementPeriod } from '../api';

/* ---------------------------------------------------------------- numbers */

const SCALE = 9;

/** An exact decimal as an integer at nine places. Anything that does not read is nought. */
export const exactOf = (amount: string | number): bigint => toMinor(amount, SCALE) ?? 0n;

/** An integer at nine places as the shortest exact decimal: 1800000000000n gives "1800". */
export const decimalOf = (scaled: bigint): string => {
  const text = minorToDecimal(scaled, SCALE);
  return text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text;
};

const abs = (value: bigint): bigint => (value < 0n ? -value : value);

/* ------------------------------------------------------------------ FIFO */

/** A line that is still open: a charge not fully paid, or a payment that pays no charge yet. */
export type OpenLine = {
  readonly id: string;
  /** What is still open of it, above nought, at nine places. */
  readonly open: bigint;
};

/** One match FIFO decided: how much of `lineId` the new line pays, or is paid by. */
export type FifoMatch = { readonly lineId: string; readonly amount: bigint };

/**
 * What a new line of `amount` (above nought) matches, given the lines of the
 * other side that are open, already in the order they are matched: the
 * oldest first (L21). Stops when the new line is used up; what is left of it
 * stays open (an advance, or a charge still due).
 */
export const fifoMatches = (amount: bigint, open: readonly OpenLine[]): FifoMatch[] => {
  const out: FifoMatch[] = [];
  let left = amount;
  for (const line of open) {
    if (left <= 0n) break;
    if (line.open <= 0n) continue;
    const take = line.open < left ? line.open : left;
    out.push({ lineId: line.id, amount: take });
    left -= take;
  }
  return out;
};

/* ------------------------------------------------------------- statement */

/** One live line of a party on one account, as the server reads it, oldest first. */
export type PartyLine = {
  readonly lineId: string;
  readonly entryId: string | null;
  readonly recordId: string | null;
  readonly recordName: string | null;
  readonly title: string | null;
  readonly on: string;
  /** Signed: debit positive (L7). */
  readonly amount: string;
  readonly unit: string;
  readonly auto: boolean;
};

/** One live match between two lines of the same party and account. */
export type PartyMatch = {
  readonly id: string;
  readonly debitLineId: string;
  readonly creditLineId: string;
  readonly amount: string;
  readonly movedBy: string | null;
};

export type PartyStatement = {
  readonly lines: StatementLine[];
  readonly charged: string;
  readonly paid: string;
  readonly balance: string;
  readonly due: string;
  readonly openCharges: number;
  readonly advance: string;
  readonly periods: StatementPeriod[];
};

/**
 * A party's statement from its live lines on one account and their matches.
 * `sign` is 1 for an account the party owes on (an asset: the receivable,
 * whose charge is a debit) and -1 for one the business owes on (a liability:
 * the payable, whose charge is a credit). Lines come oldest first; the
 * running balance follows that order.
 */
export const partyStatement = (lines: readonly PartyLine[], matches: readonly PartyMatch[], sign: 1 | -1): PartyStatement => {
  const byId = new Map(lines.map((line) => [line.lineId, line]));
  const matchedOn = new Map<string, bigint>();
  const views = new Map<string, LineMatchView[]>();
  const view = (match: PartyMatch, other: PartyLine): LineMatchView => ({
    id: match.id,
    lineId: other.lineId,
    entryId: other.entryId,
    recordId: other.recordId,
    recordName: other.recordName,
    title: other.title,
    on: other.on,
    amount: decimalOf(exactOf(match.amount)),
    movedBy: match.movedBy,
  });
  for (const match of matches) {
    const debit = byId.get(match.debitLineId);
    const credit = byId.get(match.creditLineId);
    if (debit === undefined || credit === undefined) continue;
    const amount = exactOf(match.amount);
    for (const [one, other] of [
      [debit, credit],
      [credit, debit],
    ] as const) {
      matchedOn.set(one.lineId, (matchedOn.get(one.lineId) ?? 0n) + amount);
      views.set(one.lineId, [...(views.get(one.lineId) ?? []), view(match, other)]);
    }
  }

  let balance = 0n;
  let charged = 0n;
  let paid = 0n;
  let due = 0n;
  let advance = 0n;
  let openCharges = 0;
  const periods = new Map<string, { charged: bigint; paid: bigint }>();
  const out: StatementLine[] = lines.map((line) => {
    const signed = exactOf(line.amount) * BigInt(sign);
    const amount = abs(signed);
    const kind: StatementLine['kind'] = signed > 0n ? 'charge' : 'payment';
    const matched = matchedOn.get(line.lineId) ?? 0n;
    const open = amount > matched ? amount - matched : 0n;
    balance += signed;
    if (kind === 'charge') {
      charged += amount;
      due += open;
      if (open > 0n) openCharges += 1;
      const period = line.on.slice(0, 7);
      const held = periods.get(period) ?? { charged: 0n, paid: 0n };
      held.charged += amount;
      held.paid += amount - open;
      periods.set(period, held);
    } else {
      paid += amount;
      advance += open;
    }
    return {
      lineId: line.lineId,
      entryId: line.entryId,
      recordId: line.recordId,
      recordName: line.recordName,
      title: line.title,
      on: line.on,
      kind,
      amount: decimalOf(amount),
      balance: decimalOf(balance),
      open: decimalOf(open),
      matches: views.get(line.lineId) ?? [],
      auto: line.auto,
    };
  });
  return {
    lines: out,
    charged: decimalOf(charged),
    paid: decimalOf(paid),
    balance: decimalOf(balance),
    due: decimalOf(due),
    openCharges,
    advance: decimalOf(advance),
    periods: [...periods.entries()]
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([period, held]) => {
        const left = held.charged - held.paid;
        return {
          period,
          charged: decimalOf(held.charged),
          paid: decimalOf(held.paid),
          due: decimalOf(left),
          state: left === 0n ? 'paid' : held.paid === 0n ? 'due' : 'part',
        };
      }),
  };
};
